import { useState, type FormEvent } from 'react';
import { getErrorMessage } from '../../lib/errors';
import { useAuth } from '../auth/useAuth';

const MIN_PASSWORD_LENGTH = 8;

/**
 * Change the password of the signed-in account. Delegated to Supabase's
 * `updateUser`, like the recovery-link flow; nothing here touches the
 * backend. The fields are never mirrored to storage.
 */
export function PasswordSettingsForm() {
  const { user, updatePassword, signOut } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setNotice(null);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }
    setError(null);
    setIsSaving(true);
    try {
      await updatePassword(password);
      setPassword('');
      setConfirm('');
      setNotice('Password changed.');
    } catch (caught) {
      setError(getErrorMessage(caught, 'Could not change the password. Try again.'));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <section className="wizard settings-card">
      <div className="settings-card__head">
        <h2 className="wizard__title">Sign-in</h2>
        <p className="settings-card__lede">
          Signed in as <span className="settings__inline-mono">{user?.email ?? 'unknown'}</span>.
          The address is fixed; the password is yours to change.
        </p>
      </div>

      <form className="brief-form" noValidate onSubmit={(event) => void handleSubmit(event)}>
        <div className="brief-form__stack">
          <label className="field">
            <span className="field__label">New password</span>
            <input
              className="field__input"
              type="password"
              autoComplete="new-password"
              minLength={MIN_PASSWORD_LENGTH}
              value={password}
              onChange={(event) => {
                setError(null);
                setPassword(event.target.value);
              }}
            />
            <span className="field__hint">At least {MIN_PASSWORD_LENGTH} characters.</span>
          </label>

          <label className="field">
            <span className="field__label">Confirm new password</span>
            <input
              className="field__input"
              type="password"
              autoComplete="new-password"
              minLength={MIN_PASSWORD_LENGTH}
              value={confirm}
              onChange={(event) => {
                setError(null);
                setConfirm(event.target.value);
              }}
            />
          </label>
        </div>

        {error ? (
          <p className="form__error" role="alert">
            {error}
          </p>
        ) : null}
        {notice ? (
          <p className="form__notice" role="status">
            {notice}
          </p>
        ) : null}

        <div className="wizard__actions">
          <button className="button button--ghost" type="button" onClick={() => void signOut()}>
            Sign out
          </button>
          <button
            className="button button--primary"
            type="submit"
            disabled={isSaving || !password || !confirm}
          >
            {isSaving ? 'Saving' : 'Change password'}
          </button>
        </div>
      </form>
    </section>
  );
}
